import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { ApiError } from "@njbsoft/api-client";
import {
  Badge,
  Breadcrumbs,
  Button,
  Card,
  DataTable,
  EmptyState,
  EmptyValue,
  PageHeader,
  PageSkeleton,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { useActiveMembership, useMyCompanyContext } from "@/hooks/use-company-context";
import { useIsCompanyAdmin } from "@/hooks/use-company-role";
import {
  companyKitRights,
  useCompanyTestKit,
  useDeleteTestKit,
  useKitCalibrations,
  useDownloadKitCalibrationEvidence,
  type TestKit,
  type TestKitCalibration,
} from "@/hooks/use-company-test-kits";
import { AddCalibrationDialog } from "@/components/kits/add-calibration-dialog";
import { CalibrationExpiryBadge } from "@/components/kits/calibration-expiry-badge";
import { formatDateOnly } from "@/lib/dates";
import { triggerDownload } from "@/lib/download";

export const Route = createFileRoute("/_company/company/kits/$kitId/")({
  component: KitDetailPage,
});

function statusVariant(status: TestKit["status"]): "success" | "warning" | "default" {
  if (status === "in_service") return "success";
  if (status === "out_of_service") return "warning";
  return "default";
}

const calColumnHelper = createColumnHelper<TestKitCalibration>();

function KitDetailPage() {
  const { t } = useTranslation(["company", "common"]);
  const navigate = useNavigate();
  const { kitId } = Route.useParams();
  const toasts = useToast();
  const active = useActiveMembership();
  const isAdmin = useIsCompanyAdmin();
  const companyId = active?.company_id ?? "";
  const context = useMyCompanyContext();
  const selfProfileId = context.data?.profile.id ?? "";

  const kitQuery = useCompanyTestKit(companyId, kitId);
  const calibrationsQuery = useKitCalibrations(companyId, kitId);
  const deleteMutation = useDeleteTestKit(companyId);
  const downloadMutation = useDownloadKitCalibrationEvidence(companyId);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: "calibrated_at", desc: true }]);
  // The download mutation is shared across every evidence button; track the
  // in-flight evidence id so only the clicked button shows its spinner.
  const [downloadingEvidenceId, setDownloadingEvidenceId] = useState<string | null>(null);

  const calibrations = useMemo(
    () => calibrationsQuery.data?.items ?? [],
    [calibrationsQuery.data?.items],
  );

  async function handleDownload(calibrationId: string, evidenceId: string, fileName: string) {
    setDownloadingEvidenceId(evidenceId);
    try {
      const blob = await downloadMutation.mutateAsync({ kitId, calibrationId, evidenceId });
      triggerDownload(blob, fileName);
    } catch {
      toasts.error("company:kits.downloadFailed");
    } finally {
      setDownloadingEvidenceId(null);
    }
  }

  const calColumns = useMemo(
    () => [
      calColumnHelper.accessor("calibrated_at", {
        id: "calibrated_at",
        header: t("company:kits.calColumns.calibratedAt"),
        enableSorting: true,
        cell: (ctx) => <span className="font-medium">{formatDateOnly(ctx.getValue())}</span>,
      }),
      calColumnHelper.accessor("calibration_expires_at", {
        id: "calibration_expires_at",
        header: t("company:kits.calColumns.expiresAt"),
        enableSorting: true,
        cell: (ctx) => (
          <span className="inline-flex items-center gap-2">
            {formatDateOnly(ctx.getValue())}
            <CalibrationExpiryBadge expiresAt={ctx.getValue()} />
          </span>
        ),
      }),
      calColumnHelper.accessor("calibrated_by", {
        id: "calibrated_by",
        header: t("company:kits.calColumns.calibratedBy"),
        enableSorting: true,
        cell: (ctx) => ctx.getValue(),
      }),
      calColumnHelper.accessor((row) => row.calibration_standard ?? "", {
        id: "calibration_standard",
        header: t("company:kits.calColumns.standard"),
        enableSorting: true,
        cell: (ctx) => ctx.getValue() || "—",
      }),
      calColumnHelper.accessor((row) => row.certificate_number ?? "", {
        id: "certificate_number",
        header: t("company:kits.calColumns.certificate"),
        enableSorting: true,
        cell: (ctx) => ctx.getValue() || "—",
      }),
      calColumnHelper.display({
        id: "evidence",
        header: t("company:kits.calColumns.evidence"),
        enableSorting: false,
        cell: ({ row }) => {
          const docs = row.original.evidence ?? [];
          if (docs.length === 0) return <span className="text-muted">—</span>;
          return (
            <div className="flex flex-wrap gap-2">
              {docs.map((doc) => (
                <Button
                  key={doc.id}
                  size="sm"
                  variant="secondary"
                  loading={downloadingEvidenceId === doc.id}
                  onClick={() => void handleDownload(row.original.id, doc.id, doc.file_name)}
                >
                  {t("company:kits.download")}
                </Button>
              ))}
            </div>
          );
        },
      }),
    ],
    // handleDownload closes over the stable mutation; downloadingEvidenceId
    // drives the per-button spinner and belongs in the dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, downloadingEvidenceId],
  );

  if (!active) return null;

  if (kitQuery.isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  const kit = kitQuery.data;
  if (kitQuery.isError || !kit) {
    return (
      <div className="space-y-6 p-8">
        <Breadcrumbs items={[{ label: t("company:kits.title"), to: "/company/kits" }]} />
        <PageHeader title={t("company:kits.loadDetailFailed")} />
        <EmptyState
          title={t("company:kits.loadDetailFailed")}
          actionLabel={t("company:kits.title")}
          onAction={() => void navigate({ to: "/company/kits" })}
        />
      </div>
    );
  }

  // Per-row authority: an admin acts on every gauge the company owns; a member
  // edits and calibrates the ones they HOLD, and retires none of them.
  const rights = companyKitRights(kit, { isAdmin, profileId: selfProfileId });

  const handleDelete = () => {
    deleteMutation.mutate(kitId, {
      onSuccess: () => {
        toasts.success("company:kits.deleteSuccess");
        setConfirmDelete(false);
        void navigate({ to: "/company/kits" });
      },
      onError: (err) => {
        toasts.apiError(err instanceof ApiError ? err.code : undefined);
        setConfirmDelete(false);
      },
    });
  };

  return (
    <div className="space-y-6 p-8">
      <Breadcrumbs
        items={[
          { label: t("company:kits.title"), to: "/company/kits" },
          { label: kit.serial_number },
        ]}
      />
      <PageHeader
        title={kit.serial_number}
        meta={
          <Badge variant={statusVariant(kit.status)}>
            {t(`company:kits.status.${kit.status}`)}
          </Badge>
        }
        actions={
          // Edit follows the row's holder; retiring a gauge from the inventory
          // is admin-only. The detail, the calibration ledger and its evidence
          // downloads read for every member.
          rights.canEdit || rights.canDelete ? (
            <div className="flex items-center gap-2">
              {rights.canEdit && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    void navigate({ to: "/company/kits/$kitId/edit", params: { kitId } })
                  }
                >
                  {t("common:edit")}
                </Button>
              )}
              {rights.canDelete && (
                <Button
                  variant="danger"
                  onClick={() => setConfirmDelete(true)}
                  disabled={deleteMutation.isPending}
                >
                  {t("company:kits.deleteKit")}
                </Button>
              )}
            </div>
          ) : undefined
        }
      />

      <Card title={t("company:kits.title")}>
        <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted">{t("company:kits.fields.manufacturer")}</dt>
            <dd className="text-on-surface">{kit.manufacturer}</dd>
          </div>
          <div>
            <dt className="text-muted">{t("company:kits.fields.model")}</dt>
            <dd className="text-on-surface">{kit.model || <EmptyValue />}</dd>
          </div>
          <div>
            <dt className="text-muted">{t("company:kits.fields.serialNumber")}</dt>
            <dd className="font-mono text-on-surface">{kit.serial_number}</dd>
          </div>
          <div>
            <dt className="text-muted">{t("company:kits.fields.kitType")}</dt>
            <dd className="text-on-surface">{kit.kit_type || <EmptyValue />}</dd>
          </div>
          <div>
            <dt className="text-muted">{t("company:kits.fields.status")}</dt>
            <dd className="text-on-surface">{t(`company:kits.status.${kit.status}`)}</dd>
          </div>
          <div>
            <dt className="text-muted">{t("company:kits.fields.holder")}</dt>
            <dd className="text-on-surface">{kit.holder_name || <EmptyValue />}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-muted">{t("company:kits.fields.notes")}</dt>
            <dd className="whitespace-pre-line text-on-surface">{kit.notes || <EmptyValue />}</dd>
          </div>
        </dl>
      </Card>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-on-surface">
            {t("company:kits.calibrations")}
          </h2>
          {/* Keeping a gauge current is the work of whoever carries it, so the
              append follows the same authority as the edit; the ledger itself
              reads for every member. */}
          {rights.canAppendCalibration && (
            <Button onClick={() => setAddOpen(true)}>{t("company:kits.addCalibration")}</Button>
          )}
        </div>
        {calibrationsQuery.isLoading ? (
          <Card>
            <PageSkeleton />
          </Card>
        ) : calibrations.length === 0 ? (
          <Card>
            <EmptyState variant="inline" title={t("company:kits.calibrationsEmpty")} />
          </Card>
        ) : (
          <DataTable
            columns={calColumns}
            data={calibrations}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting={false}
          />
        )}
      </section>

      <ConfirmDialog
        open={confirmDelete}
        title={t("company:kits.deleteKit")}
        message={t("company:kits.deleteConfirm")}
        confirmLabel={t("company:kits.deleteKit")}
        cancelLabel={t("common:cancel")}
        variant="danger"
        confirmDisabled={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <AddCalibrationDialog
        companyId={companyId}
        kitId={kitId}
        open={addOpen}
        onClose={() => setAddOpen(false)}
      />
    </div>
  );
}
